import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import styles from './AddCustomerModal.module.css';
import { COUNTRY_CODES, matchCountryCode } from '../util/countries';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Customer } from '../types';
import { toast } from 'react-toastify';

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedCustomer?: Customer | null;
  showDeltaPay?: boolean;
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({ isOpen, onClose, onSuccess, selectedCustomer, showDeltaPay }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cellNumber, setCellNumber] = useState('');
  const [gender, setGender] = useState('');
  const [email, setEmail] = useState('');
  const [deltaPayUsername, setDeltaPayUsername] = useState('');
  const [countryKey, setCountryKey] = useState(COUNTRY_CODES[0].key);
  const countryCode = (COUNTRY_CODES.find((code) => code.key === countryKey) ?? COUNTRY_CODES[0]).value;
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (selectedCustomer) {
      setFirstName(selectedCustomer.first_name);
      setLastName(selectedCustomer.last_name);
      const matchedCode = matchCountryCode(selectedCustomer.cell_number);
      if (matchedCode) {
        setCountryKey(matchedCode.key);
        setCellNumber(selectedCustomer.cell_number.slice(matchedCode.value.length));
      } else {
        setCountryKey(COUNTRY_CODES[0].key);
        setCellNumber(selectedCustomer.cell_number);
      }
      setGender(selectedCustomer.gender);
      setEmail(selectedCustomer.email);
      setDeltaPayUsername(selectedCustomer.delta_pay_username || '');
    } else if (!isOpen) {
      setFirstName('');
      setLastName('');
      setCellNumber('');
      setGender('');
      setEmail('');
      setDeltaPayUsername('');
      setCountryKey(COUNTRY_CODES[0].key);
    }
    if (!isOpen) {
      setErrors({});
    }
  }, [isOpen, selectedCustomer]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const newErrors: { [key: string]: string } = {};
    if (!firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!lastName.trim()) newErrors.lastName = 'Last name is required.';
    if (!cellNumber.trim()) newErrors.cellNumber = 'Cell number is required.';
    if (!email.trim()) {
      newErrors.email = 'Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        newErrors.email = 'Invalid email address.';
      }
    }
    // Compose full cell number and validate it
    const fullCellNumber = `${countryCode}${cellNumber}`;
    const validCellRegex = /^\+[0-9]+$/;
    if (!validCellRegex.test(fullCellNumber)) {
      newErrors.cellNumber = 'Cell number must start with + and contain only digits.';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const customerData = {
      first_name: firstName,
      last_name: lastName,
      cell_number: fullCellNumber,
      gender: gender || null,
      email: email,
      delta_pay_username: deltaPayUsername || null
    };

    try {
      const response = selectedCustomer 
        ? await fetchWithAuth(`/customers/${selectedCustomer.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('jwt')}`
            },
            body: JSON.stringify(customerData)
          })
        : await fetchWithAuth('/customers', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('jwt')}`
            },
            body: JSON.stringify(customerData)
          });

      if (response.ok) {
        onSuccess();
        onClose();
        toast.success(selectedCustomer ? 'Customer updated successfully' : 'Customer added successfully');
      } else {
        console.error('Failed to add/edit customer');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">{selectedCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-gray-700">First Name</label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
        </div>
        <div>
          <label htmlFor="lastName" className="block text-gray-700">Last Name</label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
        </div>
        <div>
          <label htmlFor="cellNumber" className="block text-gray-700">Cell Number</label>
          <div className="flex space-x-2">
            <select
              value={countryKey}
              onChange={(e) => setCountryKey(e.target.value)}
              className="w-1/2 px-2 py-2 border border-gray-300 rounded-md"
            >
              {COUNTRY_CODES.map((code) => (
                <option key={code.key} value={code.key}>
                  {code.label}
                </option>
              ))}
            </select>
            <input
              id="cellNumber"
              type="text"
              value={cellNumber}
              onChange={(e) => setCellNumber(e.target.value)}
              className="w-1/2 px-3 py-2 border rounded"
            />
          </div>
          {errors.cellNumber && <p className="text-red-500 text-sm mt-1">{errors.cellNumber}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Gender (Optional)</label>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          >
            <option value="">Select...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Business">Business</option>
          </select>
          {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
        </div>
        <div>
          <label htmlFor="email" className="block text-gray-700">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
        {showDeltaPay && (
          <div>
            <label htmlFor="deltaPayUsername" className="block text-gray-700">DeltaPay Username (Optional)</label>
            <input
              id="deltaPayUsername"
              type="text"
              value={deltaPayUsername}
              onChange={(e) => setDeltaPayUsername(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              placeholder="Enter DeltaPay username"
            />
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <button type="button" onClick={onClose} className="bg-gray-300 text-gray-700 px-4 py-2 rounded">Cancel</button>
          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">{selectedCustomer ? 'Update Customer' : 'Add Customer'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default AddCustomerModal;
